<template>
  <div class="candidato-detalhes">
    <!-- Header com navegação -->
    <div class="page-header">
      <div class="header-left">
        <button @click="goBack" class="btn btn-secondary">
          <i class="fas fa-arrow-left"></i>
          Voltar
        </button>
        <div class="header-info">
          <h1 class="page-title">
            <i class="fas fa-user"></i>
            Detalhes do Candidato
          </h1>
          <p class="page-subtitle" v-if="candidato.nome">{{ candidato.nome }}</p>
        </div>
      </div>
      <div class="header-actions" v-if="candidato.id">
        <button 
          @click="toggleStatus" 
          :class="candidato.status === 'A' ? 'btn btn-warning' : 'btn btn-success'"
        >
          <i :class="candidato.status === 'A' ? 'fas fa-ban' : 'fas fa-check'"></i>
          {{ candidato.status === 'A' ? 'Desativar' : 'Ativar' }}
        </button>
        <button 
          @click="openEditModal" 
          class="btn btn-primary"
        >
          <i class="fas fa-edit"></i>
          Editar Dados
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      <p>Carregando dados do candidato...</p>
    </div>

    <div v-else-if="candidato.id" class="content-grid">
      <!-- Informações Pessoais -->
      <div class="info-card">
        <div class="card-header">
          <h3><i class="fas fa-user"></i> Informações Pessoais</h3>
        </div>
        <div class="card-body">
          <div class="info-grid">
            <div class="info-item">
              <label>Nome Completo</label>
              <span>{{ candidato.nome }}</span>
            </div>
            <div class="info-item">
              <label>CPF</label>
              <span>{{ formatCPF(candidato.cpf) }}</span>
            </div>
            <div class="info-item">
              <label>RG</label>
              <span>{{ candidato.rg || '-' }}</span>
            </div>
            <div class="info-item">
              <label>Data de Nascimento</label>
              <span>{{ formatDate(candidato.data_nascimento) }}</span>
            </div>
            <div class="info-item">
              <label>Sexo</label>
              <span>{{ getSexoText(candidato.sexo) }}</span>
            </div>
            <div class="info-item">
              <label>Estado Civil</label>
              <span>{{ getEstadoCivilText(candidato.estado_civil) }}</span>
            </div>
            <div class="info-item">
              <label>Nacionalidade</label>
              <span>{{ candidato.nacionalidade || '-' }}</span>
            </div>
            <div class="info-item">
              <label>Naturalidade</label>
              <span>{{ candidato.natural_cidade }}/{{ candidato.natural_uf }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Informações de Contato -->
      <div class="info-card">
        <div class="card-header">
          <h3><i class="fas fa-phone"></i> Informações de Contato</h3>
        </div>
        <div class="card-body">
          <div class="info-grid">
            <div class="info-item">
              <label>E-mail</label>
              <span>{{ candidato.email }}</span>
            </div>
            <div class="info-item">
              <label>Telefone Celular</label>
              <span>{{ candidato.telefone_celular || '-' }}</span>
            </div>
            <div class="info-item">
              <label>Telefone Residencial</label>
              <span>{{ candidato.telefone_residencial || '-' }}</span>
            </div>
            <div class="info-item">
              <label>Telefone Comercial</label>
              <span>{{ candidato.telefone_comercial || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Endereço -->
      <div class="info-card">
        <div class="card-header">
          <h3><i class="fas fa-map-marker-alt"></i> Endereço</h3>
        </div>
        <div class="card-body">
          <div class="info-grid">
            <div class="info-item full-width">
              <label>Endereço Completo</label>
              <span>{{ getEnderecoCompleto() }}</span>
            </div>
            <div class="info-item">
              <label>CEP</label>
              <span>{{ formatCEP(candidato.cep) }}</span>
            </div>
            <div class="info-item">
              <label>UF</label>
              <span>{{ candidato.uf }}</span>
            </div>
            <div class="info-item">
              <label>Cidade</label>
              <span>{{ candidato.cidade }}</span>
            </div>
            <div class="info-item">
              <label>Bairro</label>
              <span>{{ candidato.bairro }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Informações Familiares -->
      <div class="info-card">
        <div class="card-header">
          <h3><i class="fas fa-users"></i> Informações Familiares</h3>
        </div>
        <div class="card-body">
          <div class="info-grid">
            <div class="info-item">
              <label>Nome da Mãe</label>
              <span>{{ candidato.nome_mae || '-' }}</span>
            </div>
            <div class="info-item">
              <label>Nome do Pai</label>
              <span>{{ candidato.nome_pai || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Informações Especiais -->
      <div class="info-card">
        <div class="card-header">
          <h3><i class="fas fa-wheelchair"></i> Informações Especiais</h3>
        </div>
        <div class="card-body">
          <div class="info-grid">
            <div class="info-item">
              <label>Possui Deficiência</label>
              <span :class="getDeficienciaClass()">
                {{ getDeficienciaText() }}
              </span>
            </div>
            <div class="info-item full-width" v-if="candidato.possui_deficiencia === 'S'">
              <label>Detalhe da Deficiência</label>
              <span>{{ candidato.detalhe_deficiencia || '-' }}</span>
            </div>
            <div class="info-item">
              <label>Possui Condição Especial</label>
              <span :class="getCondicaoEspecialClass()">
                {{ getCondicaoEspecialText() }}
              </span>
            </div>
            <div class="info-item full-width" v-if="candidato.possui_condicao_especial === 'S'">
              <label>Detalhe da Condição Especial</label>
              <span>{{ candidato.detalhe_condicao_especial || '-' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Estatísticas
      <div class="info-card">
        <div class="card-header">
          <h3><i class="fas fa-chart-bar"></i> Estatísticas</h3>
        </div>
        <div class="card-body">
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-value">{{ candidato.estatisticas.total_inscricoes }}</div>
              <div class="stat-label">Total de Inscrições</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ candidato.estatisticas.inscricoes_ativas }}</div>
              <div class="stat-label">Inscrições Ativas</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ candidato.estatisticas.inscricoes_canceladas }}</div>
              <div class="stat-label">Inscrições Canceladas</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ formatDate(candidato.estatisticas.ultima_inscricao) }}</div>
              <div class="stat-label">Última Inscrição</div>
            </div>
          </div>
        </div>
      </div> -->

      <!-- Histórico de Inscrições -->
      <!-- <div class="info-card full-width">
        <div class="card-header">
          <h3><i class="fas fa-file-alt"></i> Histórico de Inscrições</h3>
        </div>
        <div class="card-body">
          <div v-if="candidato.inscricoes.length === 0" class="empty-state">
            <i class="fas fa-file-alt"></i>
            <p>Nenhuma inscrição encontrada</p>
          </div>
          <div v-else class="inscricoes-table">
            <table class="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Concurso</th>
                  <th>Prefeitura</th>
                  <th>Cargo</th>
                  <th>Data</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="inscricao in candidato.inscricoes" :key="inscricao.id">
                  <td>{{ inscricao.id }}</td>
                  <td>{{ inscricao.concurso_titulo }}</td>
                  <td>{{ inscricao.prefeitura }}</td>
                  <td>{{ inscricao.cargo }}</td>
                  <td>{{ formatDate(inscricao.created_at) }}</td>
                  <td>
                    <span :class="getInscricaoStatusClass(inscricao.status)">
                      {{ getInscricaoStatusText(inscricao.status) }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div> -->
    </div>

    <div v-else class="error-state">
      <i class="fas fa-exclamation-triangle"></i>
      <h3>Candidato não encontrado</h3>
      <p>O candidato solicitado não foi encontrado ou não existe.</p>
      <button @click="goBack" class="btn btn-primary">
        <i class="fas fa-arrow-left"></i>
        Voltar à Lista
      </button>
    </div>

    <!-- Modal de Edição -->
    <div v-if="showEditModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-edit"></i>
            Editar Dados do Candidato
          </h4>
          <button @click="closeEditModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveCandidato">
            <!-- Informações Pessoais -->
            <div class="form-section">
              <h5 class="section-title"><i class="fas fa-user"></i> Informações Pessoais</h5>
              
              <div class="form-row">
                <div class="form-group">
                  <label>Nome Completo *</label>
                  <input 
                    v-model="editForm.nome" 
                    type="text" 
                    class="form-control" 
                    required
                  />
                </div>
                <div class="form-group">
                  <label>CPF *</label>
                  <input 
                    v-model="editForm.cpf" 
                    type="text" 
                    class="form-control" 
                    v-mask="'###.###.###-##'"
                    required
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>RG</label>
                  <input 
                    v-model="editForm.rg" 
                    type="text" 
                    class="form-control"
                  />
                </div>
                <div class="form-group">
                  <label>Órgão Emissor</label>
                  <input 
                    v-model="editForm.rg_orgao_emissor" 
                    type="text" 
                    class="form-control"
                  />
                </div>
                <div class="form-group">
                  <label>Data de Emissão RG</label>
                  <input 
                    v-model="editForm.rg_data_emissao" 
                    type="date" 
                    class="form-control"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Data de Nascimento</label>
                  <input 
                    v-model="editForm.data_nascimento" 
                    type="date" 
                    class="form-control"
                  />
                </div>
                <div class="form-group">
                  <label>Sexo</label>
                  <select v-model="editForm.sexo" class="form-control">
                    <option value="">Selecione</option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Estado Civil</label>
                  <select v-model="editForm.estado_civil" class="form-control">
                    <option value="">Selecione</option>
                    <option value="solteiro">Solteiro(a)</option>
                    <option value="casado">Casado(a)</option>
                    <option value="divorciado">Divorciado(a)</option>
                    <option value="viúvo">Viúvo(a)</option>
                  </select>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Nacionalidade</label>
                  <input 
                    v-model="editForm.nacionalidade" 
                    type="text" 
                    class="form-control"
                    placeholder="Ex: Brasileiro"
                  />
                </div>
                <div class="form-group">
                  <label>Naturalidade - UF</label>
                  <select v-model="editForm.natural_uf" class="form-control">
                    <option value="">Selecione</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Naturalidade - Cidade</label>
                  <input 
                    v-model="editForm.natural_cidade" 
                    type="text" 
                    class="form-control"
                  />
                </div>
              </div>
            </div>

            <!-- Informações de Contato -->
            <div class="form-section">
              <h5 class="section-title"><i class="fas fa-phone"></i> Informações de Contato</h5>
              
              <div class="form-row">
                <div class="form-group">
                  <label>E-mail *</label>
                  <input 
                    v-model="editForm.email" 
                    type="email" 
                    class="form-control" 
                    required
                  />
                </div>
                <div class="form-group">
                  <label>Telefone Celular</label>
                  <input 
                    v-model="editForm.telefone_celular" 
                    type="text" 
                    class="form-control" 
                    v-mask="'(##) #####-####'"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Telefone Residencial</label>
                  <input 
                    v-model="editForm.telefone_residencial" 
                    type="text" 
                    class="form-control" 
                    v-mask="'(##) ####-####'"
                  />
                </div>
                <div class="form-group">
                  <label>Telefone Comercial</label>
                  <input 
                    v-model="editForm.telefone_comercial" 
                    type="text" 
                    class="form-control" 
                    v-mask="'(##) ####-####'"
                  />
                </div>
              </div>
            </div>

            <!-- Endereço -->
            <div class="form-section">
              <h5 class="section-title"><i class="fas fa-map-marker-alt"></i> Endereço</h5>
              
              <div class="form-group">
                <label>CEP</label>
                <input 
                  v-model="editForm.cep" 
                  type="text" 
                  class="form-control" 
                  v-mask="'#####-###'"
                />
              </div>

              <div class="form-group">
                <label>Endereço</label>
                <input 
                  v-model="editForm.endereco" 
                  type="text" 
                  class="form-control"
                />
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Número</label>
                  <input 
                    v-model="editForm.numero" 
                    type="text" 
                    class="form-control"
                  />
                </div>
                <div class="form-group">
                  <label>Complemento</label>
                  <input 
                    v-model="editForm.complemento" 
                    type="text" 
                    class="form-control"
                  />
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Bairro</label>
                  <input 
                    v-model="editForm.bairro" 
                    type="text" 
                    class="form-control"
                  />
                </div>
                <div class="form-group">
                  <label>Cidade</label>
                  <input 
                    v-model="editForm.cidade" 
                    type="text" 
                    class="form-control"
                  />
                </div>
                <div class="form-group">
                  <label>UF</label>
                  <select v-model="editForm.uf" class="form-control">
                    <option value="">Selecione</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Filiação -->
            <div class="form-section">
              <h5 class="section-title"><i class="fas fa-users"></i> Filiação</h5>
              
              <div class="form-row">
                <div class="form-group">
                  <label>Nome da Mãe</label>
                  <input 
                    v-model="editForm.nome_mae" 
                    type="text" 
                    class="form-control"
                  />
                </div>
                <div class="form-group">
                  <label>Nome do Pai</label>
                  <input 
                    v-model="editForm.nome_pai" 
                    type="text" 
                    class="form-control"
                  />
                </div>
              </div>
            </div>

            <!-- Informações Especiais -->
            <div class="form-section">
              <h5 class="section-title"><i class="fas fa-wheelchair"></i> Informações Especiais</h5>
              
              <div class="form-row">
                <div class="form-group">
                  <label>Possui Deficiência</label>
                  <select v-model="editForm.possui_deficiencia" class="form-control">
                    <option value="">Selecione</option>
                    <option value="S">Sim</option>
                    <option value="N">Não</option>
                  </select>
                </div>
                <div class="form-group">
                  <label>Possui Condição Especial</label>
                  <select v-model="editForm.possui_condicao_especial" class="form-control">
                    <option value="">Selecione</option>
                    <option value="S">Sim</option>
                    <option value="N">Não</option>
                  </select>
                </div>
              </div>

              <div class="form-group" v-if="editForm.possui_deficiencia === 'S'">
                <label>Detalhe da Deficiência</label>
                <textarea 
                  v-model="editForm.detalhe_deficiencia" 
                  class="form-control" 
                  rows="3"
                ></textarea>
              </div>

              <div class="form-group" v-if="editForm.possui_condicao_especial === 'S'">
                <label>Detalhe da Condição Especial</label>
                <textarea 
                  v-model="editForm.detalhe_condicao_especial" 
                  class="form-control" 
                  rows="3"
                ></textarea>
              </div>
            </div>

            <!-- Status -->
            <div class="form-section">
              <h5 class="section-title"><i class="fas fa-info-circle"></i> Status</h5>
              
              <div class="form-row">
                <div class="form-group">
                  <label>Status *</label>
                  <select v-model="editForm.status" class="form-control" required>
                    <option value="A">Ativo</option>
                    <option value="I">Inativo</option>
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button @click="closeEditModal" class="btn btn-secondary">
            Cancelar
          </button>
          <button @click="saveCandidato" class="btn btn-primary" :disabled="saving">
            <i v-if="saving" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'CandidatoDetalhes',
  data() {
    return {
      loading: false,
      candidato: {},
      showEditModal: false,
      saving: false,
      editForm: {
        nome: '',
        cpf: '',
        rg: '',
        rg_orgao_emissor: '',
        rg_data_emissao: '',
        sexo: '',
        data_nascimento: '',
        estado_civil: '',
        nacionalidade: '',
        natural_uf: '',
        natural_cidade: '',
        cep: '',
        endereco: '',
        numero: '',
        complemento: '',
        bairro: '',
        cidade: '',
        uf: '',
        telefone_celular: '',
        telefone_residencial: '',
        telefone_comercial: '',
        email: '',
        nome_mae: '',
        nome_pai: '',
        possui_deficiencia: '',
        detalhe_deficiencia: '',
        possui_condicao_especial: '',
        detalhe_condicao_especial: '',
        status: 'A'
      }
    }
  },
  mounted() {
    this.loadCandidato()
  },
  methods: {
    async loadCandidato() {
      this.loading = true
      try {
        const candidatoId = this.$route.params.id
        const response = await axios.get(`/ps/candidato/admin/${candidatoId}`)
        this.candidato = response.data
      } catch (error) {
        console.error('Erro ao carregar candidato:', error)
        this.$sweet.showError('Erro ao carregar dados do candidato')
      } finally {
        this.loading = false
      }
    },

    async toggleStatus() {
      try {
        const newStatus = this.candidato.status === 'A' ? 'I' : 'A'
        const action = newStatus === 'A' ? 'ativar' : 'desativar'
        
        const { value } = await this.$sweet.showConfirm(
          'Confirmar Ação',
          `Deseja realmente ${action} o candidato ${this.candidato.nome}?`
        )

        if (value) {
          await axios.put(`/ps/candidato/admin/${this.candidato.id}/status`, {
            status: newStatus
          })

          this.candidato.status = newStatus
          this.$sweet.showSuccess(`Candidato ${action}do com sucesso!`)
        }
      } catch (error) {
        console.error('Erro ao alterar status:', error)
        this.$sweet.showError('Erro ao alterar status do candidato')
      }
    },

    goBack() {
      this.$router.push({ name: 'admin-candidatos' })
    },

    openEditModal() {
      // Preencher o formulário com os dados atuais do candidato
      this.editForm = {
        nome: this.candidato.nome || '',
        cpf: this.candidato.cpf || '',
        rg: this.candidato.rg || '',
        rg_orgao_emissor: this.candidato.rg_orgao_emissor || '',
        rg_data_emissao: this.formatDateForInput(this.candidato.rg_data_emissao),
        sexo: this.candidato.sexo || '',
        data_nascimento: this.formatDateForInput(this.candidato.data_nascimento),
        estado_civil: this.candidato.estado_civil || '',
        nacionalidade: this.candidato.nacionalidade || '',
        natural_uf: this.candidato.natural_uf || '',
        natural_cidade: this.candidato.natural_cidade || '',
        cep: this.candidato.cep || '',
        endereco: this.candidato.endereco || '',
        numero: this.candidato.numero || '',
        complemento: this.candidato.complemento || '',
        bairro: this.candidato.bairro || '',
        cidade: this.candidato.cidade || '',
        uf: this.candidato.uf || '',
        telefone_celular: this.candidato.telefone_celular || '',
        telefone_residencial: this.candidato.telefone_residencial || '',
        telefone_comercial: this.candidato.telefone_comercial || '',
        email: this.candidato.email || '',
        nome_mae: this.candidato.nome_mae || '',
        nome_pai: this.candidato.nome_pai || '',
        possui_deficiencia: this.candidato.possui_deficiencia || '',
        detalhe_deficiencia: this.candidato.detalhe_deficiencia || '',
        possui_condicao_especial: this.candidato.possui_condicao_especial || '',
        detalhe_condicao_especial: this.candidato.detalhe_condicao_especial || '',
        status: this.candidato.status || 'A'
      }
      this.showEditModal = true
    },

    closeEditModal() {
      this.showEditModal = false
    },

    async saveCandidato() {
      this.saving = true
      try {
        const candidatoId = this.$route.params.id
        
        // Preparar dados para envio, convertendo datas se necessário
        const formData = { ...this.editForm }
        
        // Converter datas do formato input (YYYY-MM-DD) para formato ISO se necessário
        if (formData.data_nascimento) {
          formData.data_nascimento = this.convertDateToISO(formData.data_nascimento)
        }
        if (formData.rg_data_emissao) {
          formData.rg_data_emissao = this.convertDateToISO(formData.rg_data_emissao)
        }
        
        await axios.put(`/ps/candidato/admin/${candidatoId}/dados`, formData)
        
        // Recarregar os dados do candidato
        await this.loadCandidato()
        
        this.$sweet.showSuccess('Dados do candidato alterados com sucesso!')
        this.closeEditModal()
      } catch (error) {
        console.error('Erro ao salvar candidato:', error)
        if (error.response?.data?.errors) {
          // Mostrar erros de validação
          const errors = Object.values(error.response.data.errors).flat()
          this.$sweet.showError(errors.join('<br>'))
        } else {
          this.$sweet.showError('Erro ao alterar dados do candidato')
        }
      } finally {
        this.saving = false
      }
    },

    formatDateForInput(date) {
      if (!date) return ''
      // Se já estiver no formato YYYY-MM-DD, retornar como está
      if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return date
      }
      // Converter de formato ISO para YYYY-MM-DD
      try {
        const dateObj = new Date(date)
        if (isNaN(dateObj.getTime())) return ''
        const year = dateObj.getFullYear()
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const day = String(dateObj.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      } catch (error) {
        return ''
      }
    },

    convertDateToISO(dateString) {
      if (!dateString) return null
      // Se já estiver no formato YYYY-MM-DD, retornar como está
      if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString
      }
      return dateString
    },

    formatCPF(cpf) {
      if (!cpf) return '-'
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },

    formatCEP(cep) {
      if (!cep) return '-'
      return cep.replace(/(\d{5})(\d{3})/, '$1-$2')
    },

    formatDate(date) {
      if (!date) return '-'
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = date.split('-')
          return `${day}/${month}/${year}`
        }
        
        // Para datas ISO com timezone, remover o 'Z' e tratar como local
        let dateString = date
        if (typeof date === 'string' && date.includes('T')) {
          // Remover o 'Z' do final se existir e tratar como hora local
          dateString = dateString.replace('Z', '')
          // Se tiver apenas data e hora sem timezone, usar diretamente
          if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split('T')
            const [year, month, day] = datePart.split('-')
            // Retornar apenas a data, ignorando a hora
            return `${day}/${month}/${year}`
          }
        }
        
        // Criar a data e formatar no fuso horário local
        const dateObj = new Date(dateString)
        
        // Verificar se a data é válida
        if (isNaN(dateObj.getTime())) return '-'
        
        // Usar métodos locais para evitar conversão UTC
        const day = String(dateObj.getDate()).padStart(2, '0')
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const year = dateObj.getFullYear()
        
        return `${day}/${month}/${year}`
      } catch (error) {
        console.error('Erro ao formatar data:', error)
        return '-'
      }
    },

    getEnderecoCompleto() {
      if (!this.candidato.endereco) return '-'
      
      const parts = [
        this.candidato.endereco,
        this.candidato.numero,
        this.candidato.complemento
      ].filter(Boolean)
      
      return parts.join(', ')
    },

    getSexoText(sexo) {
      const sexos = {
        'M': 'Masculino',
        'F': 'Feminino'
      }
      return sexos[sexo] || '-'
    },

    getEstadoCivilText(estadoCivil) {
      const estados = {
        'S': 'Solteiro(a)',
        'C': 'Casado(a)',
        'D': 'Divorciado(a)',
        'V': 'Viúvo(a)',
        'U': 'União Estável'
      }
      return estados[estadoCivil] || '-'
    },

    getDeficienciaText() {
      return this.candidato.possui_deficiencia === 'S' ? 'Sim' : 'Não'
    },

    getDeficienciaClass() {
      return {
        'badge badge-success': this.candidato.possui_deficiencia === 'N',
        'badge badge-warning': this.candidato.possui_deficiencia === 'S'
      }
    },

    getCondicaoEspecialText() {
      return this.candidato.possui_condicao_especial === 'S' ? 'Sim' : 'Não'
    },

    getCondicaoEspecialClass() {
      return {
        'badge badge-success': this.candidato.possui_condicao_especial === 'N',
        'badge badge-warning': this.candidato.possui_condicao_especial === 'S'
      }
    },

    getInscricaoStatusText(status) {
      const statuses = {
        'A': 'Ativa',
        'C': 'Cancelada'
      }
      return statuses[status] || status
    },

    getInscricaoStatusClass(status) {
      return {
        'badge badge-success': status === 'A',
        'badge badge-secondary': status === 'C'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidato-detalhes {
  .page-header {
    background: white;
    border-radius: 15px;
    padding: 25px 30px;
    margin-bottom: 20px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header-left {
      display: flex;
      align-items: center;
      gap: 20px;

      .btn {
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.3s ease;

        &.btn-secondary {
          background: #6c757d;
          border-color: #6c757d;
          color: white;

          &:hover {
            background: #5a6268;
            border-color: #5a6268;
          }
        }
      }

      .header-info {
        .page-title {
          margin: 0 0 5px 0;
          font-size: 24px;
          font-weight: 600;
          color: #2c3e50;
          display: flex;
          align-items: center;
          gap: 10px;

          i {
            color: #667eea;
          }
        }

        .page-subtitle {
          margin: 0;
          font-size: 16px;
          color: #6c757d;
          font-weight: 500;
        }
      }
    }

    .header-actions {
      .btn {
        padding: 10px 20px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.3s ease;

        &.btn-warning {
          background: #ffc107;
          border-color: #ffc107;
          color: #212529;

          &:hover {
            background: #e0a800;
            border-color: #d39e00;
          }
        }

        &.btn-success {
          background: #28a745;
          border-color: #28a745;
          color: white;

          &:hover {
            background: #218838;
            border-color: #1e7e34;
          }
        }
      }
    }
  }

  .loading-state, .error-state {
    background: white;
    border-radius: 15px;
    padding: 60px 30px;
    text-align: center;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);

    i {
      font-size: 48px;
      margin-bottom: 15px;
      opacity: 0.7;
    }

    &.loading-state i {
      color: #667eea;
    }

    &.error-state i {
      color: #dc3545;
    }

    p, h3 {
      margin: 0;
      color: #6c757d;
    }

    h3 {
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #495057;
    }

    .btn {
      margin-top: 20px;
      padding: 10px 20px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: all 0.3s ease;

      &.btn-primary {
        background: #667eea;
        border-color: #667eea;
        color: white;

        &:hover {
          background: #5a6fd8;
          border-color: #5a6fd8;
        }
      }
    }
  }

  .content-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    gap: 20px;

    .info-card {
      background: white;
      border-radius: 15px;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
      border: 1px solid rgba(0, 0, 0, 0.05);
      overflow: hidden;

      &.full-width {
        grid-column: 1 / -1;
      }

      .card-header {
        background: #f8f9fa;
        padding: 20px 25px;
        border-bottom: 1px solid #e9ecef;

        h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          display: flex;
          align-items: center;
          gap: 10px;

          i {
            color: #667eea;
          }
        }
      }

      .card-body {
        padding: 25px;

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;

          .info-item {
            &.full-width {
              grid-column: 1 / -1;
            }

            label {
              display: block;
              margin-bottom: 5px;
              font-weight: 600;
              color: #495057;
              font-size: 14px;
            }

            span {
              display: block;
              color: #6c757d;
              font-size: 14px;
              word-break: break-word;

              &.badge {
                display: inline-block;
                font-size: 12px;
                padding: 4px 8px;
                border-radius: 12px;
                font-weight: 500;

                &.badge-success {
                  background: #28a745;
                  color: white;
                }

                &.badge-warning {
                  background: #ffc107;
                  color: #212529;
                }
              }
            }
          }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 20px;

          .stat-item {
            text-align: center;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 10px;

            .stat-value {
              font-size: 24px;
              font-weight: 700;
              color: #667eea;
              margin-bottom: 5px;
            }

            .stat-label {
              font-size: 12px;
              color: #6c757d;
              font-weight: 500;
            }
          }
        }

        .empty-state {
          text-align: center;
          padding: 40px 20px;

          i {
            font-size: 32px;
            color: #6c757d;
            margin-bottom: 10px;
            opacity: 0.7;
          }

          p {
            margin: 0;
            color: #6c757d;
          }
        }

        .inscricoes-table {
          .table {
            margin: 0;
            border-collapse: separate;
            border-spacing: 0;

            thead {
              background: #f8f9fa;

              th {
                padding: 12px;
                font-weight: 600;
                color: #495057;
                border-bottom: 2px solid #e9ecef;
                font-size: 14px;
                text-align: left;
              }
            }

            tbody {
              tr {
                transition: background-color 0.3s ease;

                &:hover {
                  background: #f8f9fa;
                }

                td {
                  padding: 12px;
                  border-bottom: 1px solid #e9ecef;
                  font-size: 14px;
                  color: #495057;

                  .badge {
                    font-size: 12px;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-weight: 500;

                    &.badge-success {
                      background: #28a745;
                      color: white;
                    }

                    &.badge-secondary {
                      background: #6c757d;
                      color: white;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    // Modal Styles
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1050;

      .modal-content {
        background: white;
        border-radius: 8px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        max-height: 90vh;
        overflow-y: auto;
        width: 90%;
        max-width: 500px;

        &.large {
          max-width: 800px;
        }

        .modal-header {
          background: white !important;
          padding: 20px;
          border-bottom: 1px solid #e9ecef;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 8px 8px 0 0;

          .modal-title {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #495057 !important;
            background: transparent !important;

            i {
              margin-right: 8px;
              color: #007bff;
            }
          }

          .modal-close {
            background: none;
            border: none;
            font-size: 20px;
            color: #6c757d;
            cursor: pointer;
            padding: 0;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.3s ease;

            &:hover {
              background: #f8f9fa;
              color: #495057;
            }
          }
        }

        .modal-body {
          background: white !important;
          padding: 20px;
          max-height: 70vh;
          overflow-y: auto;

          .form-section {
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 1px solid #e9ecef;
            background: transparent !important;

            &:last-child {
              border-bottom: none;
              margin-bottom: 0;
            }

            .section-title {
              font-size: 16px;
              font-weight: 600;
              color: #495057 !important;
              margin-bottom: 20px;
              display: flex;
              align-items: center;
              gap: 8px;
              background: transparent !important;

              i {
                color: #667eea;
              }
            }
          }

          .form-row {
            display: flex;
            gap: 15px;
            margin-bottom: 15px;

            .form-group {
              flex: 1;
            }
          }

          .form-group {
            margin-bottom: 15px;

            textarea.form-control {
              resize: vertical;
              min-height: 80px;
            }

            label {
              display: block;
              margin-bottom: 5px;
              font-weight: 500;
              color: #495057 !important;
              font-size: 14px;
              background: transparent !important;
            }

            .form-control {
              width: 100%;
              padding: 8px 12px;
              border: 1px solid #ced4da;
              border-radius: 4px;
              font-size: 14px;
              transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

              &:focus {
                outline: none;
                border-color: #007bff;
                box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
              }
            }
          }
        }

        .modal-footer {
          background: white !important;
          padding: 15px 20px;
          border-top: 1px solid #e9ecef;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          border-radius: 0 0 8px 8px;

          .btn {
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            gap: 5px;

            &.btn-secondary {
              background: #6c757d;
              color: white;

              &:hover {
                background: #5a6268;
              }
            }

            &.btn-primary {
              background: #007bff;
              color: white;

              &:hover:not(:disabled) {
                background: #0056b3;
              }

              &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }
            }
          }
        }
      }
    }
  }
}

// Responsividade
@media (max-width: 768px) {
  .candidato-detalhes {
    .page-header {
      flex-direction: column;
      gap: 20px;
      align-items: stretch;

      .header-left {
        flex-direction: column;
        align-items: stretch;
        gap: 15px;
      }

      .header-actions {
        align-self: stretch;
      }
    }

    .content-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
